import { useMemo } from 'react';
import { NumericInput } from '@interstate/components/NumericInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { NameFieldsWrapper } from '../CustomerCard.styles';
import {
    BUSINESS_TYPE_OPTIONS,
    FINANCIAL_STATEMENT_TYPE_OPTIONS,
    BUSINESS_TYPE_FIELD,
    BUSINESS_ESTABLISHED_DATE_FIELD,
    BUSINESS_NUMBER_OF_EMPLOYEES_FIELD,
    BUSINESS_STATE_OF_INCORPORATION_FIELD,
    BUSINESS_FINANCIAL_STATEMENT_DESCRIPTION_FIELD,
    BUSINESS_TAX_ID_NUMBER_FIELD,
    TAX_ID_MASK
} from '../constants';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import { useCustomerCardContext } from '../CustomerCardProvider';
import { RevealableMaskTextInput } from '@makemydeal/dr-shared-components';
import type { ErrorState } from '@makemydeal/dr-dash-ui-utils';

export const BusinessInformationFields = () => {
    const { formValues, handleInputChange, handleNumberInputChange, isLocked, customerFormErrors } = useCustomerCardContext();
    const businessErrors = customerFormErrors.business as Record<string, ErrorState> | undefined;
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);

    const handleDateEstablishedChange = (event: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const value = event.target.value;
        const dateValue = value.additionalFormats?.iso ? value.additionalFormats.iso.slice(0, 10) : '';
        handleInputChange(BUSINESS_ESTABLISHED_DATE_FIELD, dateValue);
    };

    return (
        <>
            <NameFieldsWrapper columns={4}>
                <RevealableMaskTextInput
                    name={BUSINESS_TAX_ID_NUMBER_FIELD}
                    label="Tax ID Number"
                    data-testid="business-info-tax-id"
                    maskedValue={TAX_ID_MASK}
                    value={formValues.business?.taxIdNumber || ''}
                    onChange={handleInputChange}
                    disabled={isLocked}
                    hasError={Boolean(businessErrors?.taxIdNumber?.hasError)}
                    errorMessage={businessErrors?.taxIdNumber?.errorMessage || ''}
                />
                <SelectInput
                    name="business-businessType"
                    displayLabel={true}
                    label="Business Type"
                    value={formValues.business?.businessType || ''}
                    onChange={(e) => handleInputChange(BUSINESS_TYPE_FIELD, e.target.value || '')}
                    options={BUSINESS_TYPE_OPTIONS}
                    data-testid="business-info-business-type"
                    size="small"
                    disabled={isLocked}
                />
                <DatePicker
                    name="business-dateEstablished"
                    label="Date Established"
                    value={
                        formValues.business?.businessEstablishedDate
                            ? new Date(formValues.business.businessEstablishedDate)
                            : undefined
                    }
                    onChange={handleDateEstablishedChange}
                    data-testid="business-info-date-established"
                    size="small"
                    disabled={isLocked}
                />
                <NumericInput
                    id="business-numberOfEmployees"
                    name="business-numberOfEmployees"
                    label="Number of Employees"
                    value={formValues.business?.numberOfEmployees?.toString() || ''}
                    onChange={(e) => handleNumberInputChange(BUSINESS_NUMBER_OF_EMPLOYEES_FIELD, e.target.value)}
                    data-testid="business-info-number-of-employees"
                    size="small"
                    disabled={isLocked}
                    allowDecimal={false}
                    hasError={Boolean(businessErrors?.numberOfEmployees?.hasError)}
                    errorMessage={businessErrors?.numberOfEmployees?.errorMessage || ''}
                />
            </NameFieldsWrapper>
            <NameFieldsWrapper columns={4}>
                <SelectInput
                    name="business-stateOfIncorporation"
                    label="State of Inc"
                    value={formValues.business?.stateOfIncorporation || ''}
                    onChange={(e) => handleInputChange(BUSINESS_STATE_OF_INCORPORATION_FIELD, e.target.value || '')}
                    options={stateSelectInputValues}
                    data-testid="business-info-state-of-inc"
                    size="small"
                    disabled={isLocked}
                />
                <SelectInput
                    name="business-financialStatementType"
                    displayLabel={true}
                    label="Fin. Statement Type (optional)"
                    value={formValues.business?.financialStatementDescription || ''}
                    onChange={(e) => handleInputChange(BUSINESS_FINANCIAL_STATEMENT_DESCRIPTION_FIELD, e.target.value || '')}
                    options={FINANCIAL_STATEMENT_TYPE_OPTIONS}
                    data-testid="business-info-financial-statement-type"
                    size="small"
                    disabled={isLocked}
                />
            </NameFieldsWrapper>
        </>
    );
};
